'use client'

import { usePageData } from '@/contexts/PageDataContext'
import type { ImageData, Maybe } from '@/frontend-types'
import type { ComponentProps, FC, ReactNode } from 'react'
import { useId, useState } from 'react'
import { Overlay } from '../molecules/Overlay'
import { FontAwesomeIcon } from './FontAwesomeIcon'
import { ImageWithCorner } from './ImageWithCorner'
import { PlaceholderImage } from './PlaceholderImage'

export type CardProfileType = {
	image?: ImageData
	name: string
	position?: Maybe<string>
	hasDetails: boolean
	details?: ReactNode
}

export type CardProfileProps = ComponentProps<'div'> & {
	cardProfile: CardProfileType
}

export const CardProfile: FC<CardProfileProps> = ({ cardProfile, ...props }) => {
	const { image, name, position, details, hasDetails } = cardProfile
	const [open, setOpen] = useState<boolean>(false)

	const { title } = usePageData()

	const id = useId()
	const labelId = `team-item-${id}`
	const contentId = `team-item-${id}-content`

	return (
		<div {...props} className="flex flex-col gap-y-xl">
			{image ? (
				<ImageWithCorner
					image={image}
					aspectRatio="aspect-profile"
					sizes="(max-width: 1023px) 50vw, (max-width: 1559px) 25vw, 336px"
				/>
			) : (
				<PlaceholderImage />
			)}
			<div className="flex flex-col">
				<span className="text-h5">{name}</span>
				<span className="text-body-sm">{position}</span>
				{hasDetails && (
					<button
						id={labelId}
						className="flex w-fit cursor-pointer items-center gap-x-sm text-button text-blue hover:text-blue-shade-40 active:text-blue-shade-70 group"
						aria-controls={contentId}
						aria-expanded={open}
						onClick={() => setOpen(true)}
					>
						<span>Read Bio</span>
						<FontAwesomeIcon icon={['fas', 'plus']} size="md" className="text-inherit" />
					</button>
				)}
			</div>
			{hasDetails && (
				<Overlay
					titleTag={title}
					open={open}
					setOpen={setOpen}
					aria-labelledby={`${labelId} ${contentId}-close`}
					id={contentId}
				>
					{details}
				</Overlay>
			)}
		</div>
	)
}
